/**
 * The notification inbox, client side.
 *
 * Grants have worked for months and told nobody: a chart appeared in "Shared
 * with me" and you found out next time you looked. This is the half that says
 * so.
 *
 * **Polling, and why it is not extravagant.** One request every 45 seconds,
 * only while the tab is actually visible - a laptop with six forgotten tabs
 * should not be asking. That is roughly one request per user per minute against
 * an endpoint that returns at most forty small rows. It also, incidentally,
 * keeps `audit.user_session` warm, which is exactly what presence needs when
 * that gets built: the heartbeat is already here.
 */

import { Atom } from './atom.ts';
import { getNotifications, markNotificationsRead } from '../api/endpoints.ts';
import type { Notification } from '../api/types.ts';

/** How often to look, while the tab is in front of someone. */
const POLL_MS = 45_000;

export const notifications = new Atom<Notification[]>([]);
export const unreadCount = new Atom<number>(0);
/** False with no metadata database: there is no inbox and never will be, so the
 *  bell stays out of the header rather than showing an empty one forever. */
export const notificationsAvailable = new Atom<boolean>(false);
/** The newest arrival not yet shown as a toast - the bell watches this. */
export const newestArrival = new Atom<Notification | null>(null);

let timer: ReturnType<typeof setInterval> | null = null;
let seen: Set<string> | null = null;

/** Fetch once. Never throws: a bell that cannot count is not worth an error. */
export async function refreshNotifications(): Promise<void> {
  try {
    const res = await getNotifications();
    notificationsAvailable.set(res.available);
    if (!res.available) {
      notifications.set([]);
      unreadCount.set(0);
      return;
    }

    // Anything unread that this tab has not seen before is an arrival. On the
    // first load everything is "new", which would mean a toast for a week of
    // history - so the first response only establishes what is already known.
    const unread = res.items.filter((n) => !n.read);
    if (seen === null) {
      seen = new Set(unread.map((n) => n.id));
    } else {
      const arrival = unread.find((n) => !seen?.has(n.id));
      for (const n of unread) seen.add(n.id);
      if (arrival) newestArrival.set(arrival);
    }

    notifications.set(res.items);
    unreadCount.set(res.unread);
  } catch {
    // Offline, signed out, backend restarting: try again on the next tick.
  }
}

/** Mark everything, or a few, as read - locally first so the badge is instant. */
export async function markRead(ids?: string[]): Promise<void> {
  const target = ids?.length ? new Set(ids) : null;
  notifications.set(
    notifications.value.map((n) => (!target || target.has(n.id) ? { ...n, read: true } : n)),
  );
  unreadCount.set(notifications.value.filter((n) => !n.read).length);
  try {
    await markNotificationsRead(ids);
  } catch {
    // The server missed it; the next poll puts the truth back.
  }
}

function tick(): void {
  if (document.visibilityState === 'visible') void refreshNotifications();
}

/**
 * Start looking. Idempotent, so a re-render cannot start a second timer.
 *
 * Also fetches the moment a tab comes back to the front, because the interesting
 * question after lunch is "what did I miss", and waiting up to 45 seconds to
 * answer it would be a poor showing.
 */
export function startNotificationPolling(): void {
  if (timer !== null) return;
  void refreshNotifications();
  timer = setInterval(tick, POLL_MS);
  document.addEventListener('visibilitychange', tick);
}

export function stopNotificationPolling(): void {
  if (timer !== null) clearInterval(timer);
  timer = null;
  document.removeEventListener('visibilitychange', tick);
}
